export type ShiftWindow = {
  id: string;
  productive_hours: number;
  shift_sequence?: number | null;
  start_time?: string | null;
  end_time?: string | null;
};

function clockMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = String(value).match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function intervals(start: string | null | undefined, end: string | null | undefined): Array<[number, number]> {
  const from = clockMinutes(start);
  const to = clockMinutes(end);
  if (from == null || to == null || from === to) return [];
  if (to > from) return [[from, to]];
  return [[from, 24 * 60], [0, to]];
}

function overlaps(left: Array<[number, number]>, right: Array<[number, number]>): boolean {
  if (!left.length || !right.length) return false;
  return left.some(([a1, a2]) => right.some(([b1, b2]) => a1 < b2 && b1 < a2));
}

/** Same rule as backend workstation-capacity.util: overlapping shifts are not added together. */
export function nonOverlappingProductiveHours(shifts: ShiftWindow[]): number {
  const ordered = [...shifts].sort((left, right) => {
    const sequence = (Number(left.shift_sequence) || 0) - (Number(right.shift_sequence) || 0);
    if (sequence) return sequence;
    return String(left.start_time || '').localeCompare(String(right.start_time || ''));
  });
  const ceiling = ordered.reduce((sum, shift) => sum + Math.max(0, Number(shift.productive_hours) || 0), 0);
  let remaining = ceiling;
  const taken: Array<Array<[number, number]>> = [];
  let total = 0;
  for (const shift of ordered) {
    const window = intervals(shift.start_time, shift.end_time);
    if (window.length && taken.some((existing) => overlaps(existing, window))) continue;
    const room = Math.max(0, Number(shift.productive_hours) || 0);
    const give = Math.round(Math.min(remaining, room) * 100) / 100;
    total += give;
    remaining = Math.round((remaining - give) * 100) / 100;
    if (give > 0 && window.length) taken.push(window);
  }
  return Math.round(total * 100) / 100;
}
